import { useCallback, useEffect, useRef, useState } from 'react'
import { getNewsActivity } from '@domain/features/news/news-feature'
import type { ActivityExplore } from '@domain/models/activity-explore'
import { useSsrPageData } from '@presentation/ssr/useSsrPageData'
import {
  detailEntityMatchesRoute,
  isDetailLoadErrorFatal,
  keepDetailEntityForRoute,
} from './detail-load-state'

function matchesActivityRoute(activity: ActivityExplore, activityId: string): boolean {
  return activity.uid === activityId
}

export function useActivity(activityId: string | undefined) {
  const ssrActivity = useSsrPageData()?.newsDetail?.activity ?? null
  const hasMatchingSsrActivity = detailEntityMatchesRoute(
    ssrActivity,
    activityId,
    matchesActivityRoute,
  )
  const [activity, setActivityState] = useState<ActivityExplore | null>(() =>
    keepDetailEntityForRoute(ssrActivity, activityId, matchesActivityRoute))
  const [loading, setLoading] = useState(!hasMatchingSsrActivity)
  const [error, setError] = useState<string | null>(null)
  const activityRef = useRef<ActivityExplore | null>(activity)

  const setActivity = useCallback((nextActivity: ActivityExplore | null) => {
    activityRef.current = nextActivity
    setActivityState(nextActivity)
  }, [])

  useEffect(() => {
    let cancelled = false

    async function load() {
      await Promise.resolve()
      if (cancelled) {
        return
      }

      if (!activityId) {
        setActivity(null)
        setError(null)
        setLoading(false)
        return
      }

      const resolvedActivityId = activityId
      const ssrFallback = keepDetailEntityForRoute(
        ssrActivity,
        resolvedActivityId,
        matchesActivityRoute,
      )
      if (ssrFallback && !detailEntityMatchesRoute(
        activityRef.current,
        resolvedActivityId,
        matchesActivityRoute,
      )) {
        setActivity(ssrFallback)
      } else if (!detailEntityMatchesRoute(
        activityRef.current,
        resolvedActivityId,
        matchesActivityRoute,
      )) {
        setActivity(null)
      }

      try {
        if (!detailEntityMatchesRoute(
          activityRef.current,
          resolvedActivityId,
          matchesActivityRoute,
        )) {
          setLoading(true)
        }
        setError(null)
        const result = await getNewsActivity(resolvedActivityId)
        if (!cancelled) setActivity(result)
      } catch (err) {
        if (!cancelled && isDetailLoadErrorFatal(
          activityRef.current,
          resolvedActivityId,
          matchesActivityRoute,
        )) {
          setError(err instanceof Error ? err.message : 'Failed to load activity')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()
    return () => { cancelled = true }
  }, [activityId, setActivity, ssrActivity])

  return { activity, loading, error }
}
